import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useUpdateOversightAssessment } from 'src/hooks/mutations/oversight-assessment';
import AssessmentStatusPreview from 'src/pages/assessments/forms/assessment-status-preview/AssessmentStatusPreview';
import OversightAssessmentForm from 'src/pages/assessments/oversight-assessments/forms/oversight-assessment-form/OversightAssessmentForm';
import type { OversightAssessmentFormDataFields } from 'src/pages/assessments/oversight-assessments/forms/oversight-assessment-form/oversightAssessmentSchema';
import { defaultValues } from 'src/pages/assessments/oversight-assessments/forms/oversight-assessment-form/oversightAssessmentSchema';
import OversightFindingPreview from 'src/pages/assessments/oversight-assessments/forms/oversight-findings-preview/OversightFindingPreview';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { handleError } from 'src/utils/errorUtils';
import { oversightAssessmentDetailsUrl } from 'src/utils/urls';

import {
  useGetAssessmentRCSAActivitiesByAssessmentId,
  useGetOversightAssessmentById,
} from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

const Tab: FC = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'assessmentActivities',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const assessmentId = useGetGuidParam('assessmentId');
  const navigate = useNavigate();
  const {
    data,
    error,
    loading: loadingAssessment,
    refetch,
  } = useGetOversightAssessmentById({
    queryArgs: { id: assessmentId },
  });
  if (error) {
    throw error;
  }

  const oversightAssessment = data?.oversight_assessment[0];

  const { hasPermission: canEdit, loading: canEditLoading } =
    useHasPermissionQuery('update:oversight_assessment', oversightAssessment);
  const { updateOversightAssessment } = useUpdateOversightAssessment();

  const { data: activitiesRCSAData } =
    useGetAssessmentRCSAActivitiesByAssessmentId({
      queryArgs: { assessmentId },
    });

  const disabledUsers = activitiesRCSAData?.assessment_activity.map(
    (activity) => ({
      userId: activity.AssignedUser ?? '',
      reason: t('disabledUserPrompt'),
    })
  );

  const onSave = async (data: OversightAssessmentFormDataFields) => {
    if (!oversightAssessment) {
      throw new Error('Missing compliance monitoring assessment');
    }
    await updateOversightAssessment({
      Id: oversightAssessment.Id,
      // Optimistic-concurrency token: the snapshot this form was loaded from.
      // A second editor saving over a newer row is rejected with a 409 rather
      // than silently winning (COI-622).
      OriginalTimestamp: oversightAssessment.ModifiedAtTimestamp,
      OriginatingItemId: oversightAssessment.OriginatingItemId,
      CustomAttributeData: data.CustomAttributeData || undefined,
      ...ownerContributorAndViewerIds(data),
      TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
      DepartmentTypeIds: data.departments?.map((d) => d.DepartmentTypeId) || [],
      CompletedByUser: data.CompletedByUser?.value ?? null,
      Status: data.Status,
      StartDate: data.StartDate,
      Title: data.Title,
      Summary: data.Summary,
      ActualCompletionDate: data.ActualCompletionDate,
      NextTestDate: data.NextTestDate,
      TargetCompletionDate: data.TargetCompletionDate,
      Outcome: data.Outcome,
    });
    // The update has already committed. A refetch failure must not reject
    // onSave (that would read as a failed save) or strand the user on the
    // form — report it and navigate regardless.
    try {
      await refetch();
    } catch (error) {
      handleError(error);
    } finally {
      navigate(oversightAssessmentDetailsUrl(assessmentId), {
        replace: true,
      });
    }
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <OversightAssessmentForm
      readOnly={!canEdit || canEditLoading || loadingAssessment}
      values={{
        ...defaultValues,
        ...oversightAssessment,
        Summary: oversightAssessment?.Summary ?? '',
        Owners: getOwners(oversightAssessment),
        Contributors: getContributors(oversightAssessment),
        Viewers: getViewers(oversightAssessment),
        ActualCompletionDate: oversightAssessment?.ActualCompletionDate ?? null,
        NextTestDate: oversightAssessment?.NextTestDate ?? null,
        TargetCompletionDate: oversightAssessment?.TargetCompletionDate ?? null,
        StartDate: oversightAssessment?.StartDate ?? null,
        CompletedByUser: oversightAssessment?.CompletedByUser
          ? {
              value: oversightAssessment.CompletedByUser,
              type: 'user',
            }
          : null,
        ancestorContributors: oversightAssessment?.ancestorContributors ?? [],
        Status: oversightAssessment?.Status ?? 'notstarted',
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={assessmentId}
      aside={
        <>
          <AssessmentStatusPreview
            status={oversightAssessment?.Status ?? undefined}
            targetCompletionDate={oversightAssessment?.TargetCompletionDate}
            actualCompletionDate={oversightAssessment?.ActualCompletionDate}
          />
          <OversightFindingPreview
            oversightAssessmentId={oversightAssessment?.Id}
            outcome={oversightAssessment?.Outcome}
          />
        </>
      }
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      disabledUsers={disabledUsers}
    />
  );
};

export default Tab;
